import { Directive, ElementRef, HostListener, inject, input } from '@angular/core';
import { AnalyticsLinkClickProvider } from '../providers/link-click-provider';

export interface ClickEventArgs {
  /** Category of the link, sent as `field_name` (e.g. Delivery, Assessment, Product Type). */
  fieldName?: string;
  /** Selectors used to filter the actual click target when the directive is hosted on a container. */
  selectors?: {
    whitelist?: string[];
    blacklist?: string[];
  };
  /** Overrides the text derived from the clicked element. */
  customText?: string;
}

/**
 * Emits a `link_click` analytics event when the host element (or a matching descendant)
 * is clicked with the left or middle mouse button.
 *
 * Apply to `<button>`, `<a>` or any element that acts as a clickable link.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[clickEvent]',
  standalone: true,
})
export class ClickEventDirective {
  readonly eventArgs = input<ClickEventArgs>({}, { alias: 'clickEvent' });

  private readonly provider = inject(AnalyticsLinkClickProvider);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  @HostListener('mouseup', ['$event'])
  protected onMouseUp(event: MouseEvent): void {
    if (!this.isLeftOrMiddleClick(event)) return;

    const clickTarget = this.resolveClickTarget(event);
    if (!clickTarget) return;

    const args = this.eventArgs() ?? {};
    this.provider.createAndPush({
      element: clickTarget,
      customText: args.customText,
      fieldName: args.fieldName,
    });
  }

  private resolveClickTarget(event: MouseEvent): HTMLElement | null {
    const host = this.elementRef.nativeElement;
    const { whitelist, blacklist } = this.eventArgs()?.selectors ?? {};

    // Without a whitelist, the directive's host is the clickable; ignore deep children
    // like icons that happen to be the actual event target.
    if (!whitelist?.length) {
      return this.matchesBlacklist(host, blacklist) ? null : host;
    }

    // With a whitelist (delegated container usage), find the closest ancestor of the
    // event target — up to and including the host — that matches.
    const eventTarget = event.target as HTMLElement | null;
    let current: HTMLElement | null = eventTarget && host.contains(eventTarget) ? eventTarget : host;
    while (current) {
      if (
        whitelist.some((selector) => current!.matches(selector)) &&
        !this.matchesBlacklist(current, blacklist)
      ) {
        return current;
      }
      if (current === host) return null;
      current = current.parentElement;
    }
    return null;
  }

  private matchesBlacklist(target: HTMLElement, blacklist?: string[]): boolean {
    return !!blacklist?.length && blacklist.some((selector) => target.matches(selector));
  }

  private isLeftOrMiddleClick(event: MouseEvent): boolean {
    return event.button === 0 || event.button === 1;
  }
}
